// Relatório Executivo CLT × PJ — layout profissional + export PDF/PNG/DOCX.
// IMPORTANTE: consome o MESMO objeto `result` da tela — nunca recalcula.
import { useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { FileDown, FileText, Image as ImageIcon, Printer, X, Mail, Save } from "lucide-react";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { saveAs } from "file-saver";

const BRL = (v) =>
  `R$ ${Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const PCT = (v) => `${Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
const NUM = (v) => Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Props:
 *  - open, onClose
 *  - result: resposta de /api/clt-pj/simulate (mesma struct da tela)
 *  - inputs: { clt, pj } com os parâmetros originais
 *  - meta: { empresa?, cargo?, responsavel?, nomeCenario?, competenciaAno }
 */
export default function CltPjReportDialog({ open, onClose, result, inputs, meta }) {
  const reportRef = useRef(null);
  const [exporting, setExporting] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [destinatarios, setDestinatarios] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [savedReportId, setSavedReportId] = useState(null);

  const data = useMemo(() => buildReportModel(result, inputs, meta), [result, inputs, meta]);

  if (!open || !data) return null;

  // Helper: gera o PDF e retorna { blob, base64 } sem salvar em disco
  const renderPdfBlob = async () => {
    const canvas = await html2canvas(reportRef.current, {
      scale: 2.4, backgroundColor: "#ffffff", useCORS: true, logging: false,
    });
    const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const imgW = pageW - 10;
    const imgH = (canvas.height * imgW) / canvas.width;
    if (imgH <= pageH - 10) {
      pdf.addImage(canvas.toDataURL("image/jpeg", 0.92), "JPEG", 5, 5, imgW, imgH);
    } else {
      const pxPerMm = canvas.width / imgW;
      const sliceHeightPx = Math.floor((pageH - 10) * pxPerMm);
      let y = 0;
      while (y < canvas.height) {
        const h = Math.min(sliceHeightPx, canvas.height - y);
        const slice = document.createElement("canvas");
        slice.width = canvas.width; slice.height = h;
        slice.getContext("2d").drawImage(canvas, 0, y, canvas.width, h, 0, 0, canvas.width, h);
        if (y > 0) pdf.addPage("a4", "landscape");
        pdf.addImage(slice.toDataURL("image/jpeg", 0.92), "JPEG", 5, 5, imgW, h / pxPerMm);
        y += h;
      }
    }
    const blob = pdf.output("blob");
    const dataUri = pdf.output("datauristring");
    const base64 = (dataUri.split(",")[1] || "").trim();
    return { blob, base64 };
  };

  const saveToHistory = async () => {
    try {
      const body = {
        company_id: meta?.companyId || null,
        title: meta?.cargo ? `CLT × PJ · ${meta.cargo}` : "Relatório CLT × PJ",
        scenario_type: meta?.scenarios ? "multi" : "single",
        inputs, result, meta,
        scenarios: meta?.scenarios || null,
      };
      const { data: saved } = await api.post("/clt-pj/reports", body);
      setSavedReportId(saved.id);
      toast.success(`Relatório salvo no histórico (${saved.id.slice(0, 8)}…).`);
      return saved.id;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      return null;
    }
  };

  const openEmailDialog = () => {
    setDestinatarios(""); setEmailMessage("");
    setEmailOpen(true);
  };

  const sendEmail = async () => {
    const emails = destinatarios.split(/[,;\s]+/).map(e => e.trim()).filter(e => e && e.includes("@"));
    if (emails.length === 0) { toast.error("Informe ao menos um e-mail válido."); return; }
    setEmailSending(true);
    try {
      const { base64 } = await renderPdfBlob();
      // Salva no histórico se ainda não salvo
      const rid = savedReportId || await saveToHistory();
      const { data: resp } = await api.post("/clt-pj/reports/send-email", {
        destinatarios: emails,
        subject: `Relatório Comparativo CLT × PJ · ${meta?.cargo || "Diretoria"}`,
        message: emailMessage || null,
        pdf_base64: base64,
        filename: `relatorio-clt-pj-${todayStamp()}.pdf`,
        report_id: rid,
      });
      if (resp.status === "sent") {
        toast.success(`E-mail enviado para ${emails.length} destinatário(s).`);
      } else if (resp.status === "skipped_no_resend_key") {
        toast.warning("RESEND_API_KEY vazia. O envio foi registrado mas o e-mail não saiu. Configure em Emergent → Profile → Integrations.", { duration: 9000 });
      } else {
        toast.error(resp.message || "Falha ao enviar.");
      }
      setEmailOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setEmailSending(false); }
  };

  const exportPng = async () => {
    setExporting("png");
    try {
      const canvas = await html2canvas(reportRef.current, {
        scale: 3, backgroundColor: "#ffffff", useCORS: true, logging: false,
      });
      canvas.toBlob((blob) => {
        if (blob) saveAs(blob, `relatorio-clt-pj-${todayStamp()}.png`);
        setExporting(null);
        toast.success("Imagem PNG de alta resolução baixada.");
      }, "image/png");
    } catch (e) {
      setExporting(null);
      toast.error("Falha ao gerar PNG: " + e.message);
    }
  };

  const exportPdf = async () => {
    setExporting("pdf");
    try {
      const { blob } = await renderPdfBlob();
      saveAs(blob, `relatorio-clt-pj-${todayStamp()}.pdf`);
      setExporting(null);
      toast.success("PDF A4 paisagem gerado.");
    } catch (e) {
      setExporting(null);
      toast.error("Falha ao gerar PDF: " + e.message);
    }
  };

  const exportDocx = async () => {
    setExporting("docx");
    try {
      const blob = await buildDocx(data);
      saveAs(blob, `relatorio-clt-pj-${todayStamp()}.docx`);
      setExporting(null);
      toast.success("Documento Word editável gerado.");
    } catch (e) {
      setExporting(null);
      toast.error("Falha ao gerar Word: " + (e.message || e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent className="max-w-[1180px] max-h-[94vh] overflow-hidden p-0 bg-white">
        <DialogHeader className="px-5 pt-4 pb-2 border-b border-slate-200">
          <DialogTitle className="font-display text-[18px] text-[#234935]">
            Relatório Executivo · CLT × PJ
          </DialogTitle>
          <DialogDescription className="text-[11.5px]">
            Pré-visualização do relatório. Exporte em PDF (impressão/arquivamento), PNG (WhatsApp) ou Word (editável).
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-y-auto px-4 py-3 bg-slate-100" style={{ maxHeight: "70vh" }} data-testid="report-preview-area">
          <div ref={reportRef}>
            <ReportDocument data={data} />
          </div>
        </div>
        <DialogFooter className="px-5 py-3 border-t border-slate-200 bg-white flex-wrap gap-2">
          <Button variant="ghost" onClick={onClose} className="mr-auto" data-testid="btn-close-report">
            <X className="h-3.5 w-3.5 mr-1" /> Fechar
          </Button>
          <Button variant="outline" onClick={saveToHistory} disabled={!!exporting || !!savedReportId} data-testid="btn-save-history">
            <Save className="h-3.5 w-3.5 mr-1.5" /> {savedReportId ? "Salvo no histórico" : "Salvar no Histórico"}
          </Button>
          <Button variant="outline" onClick={openEmailDialog} disabled={!!exporting} data-testid="btn-send-email">
            <Mail className="h-3.5 w-3.5 mr-1.5" /> Enviar para Diretoria
          </Button>
          <Button variant="outline" onClick={exportDocx} disabled={!!exporting} data-testid="btn-export-docx">
            <FileText className="h-3.5 w-3.5 mr-1.5" /> {exporting === "docx" ? "Gerando..." : "Word (.docx)"}
          </Button>
          <Button variant="outline" onClick={exportPng} disabled={!!exporting} data-testid="btn-export-png">
            <ImageIcon className="h-3.5 w-3.5 mr-1.5" /> {exporting === "png" ? "Gerando..." : "PNG (WhatsApp)"}
          </Button>
          <Button onClick={exportPdf} disabled={!!exporting} className="bg-[#234935] hover:bg-[#2c5c43] text-white"
                  data-testid="btn-export-pdf">
            <Printer className="h-3.5 w-3.5 mr-1.5" /> {exporting === "pdf" ? "Gerando..." : "PDF (A4 paisagem)"}
          </Button>
        </DialogFooter>
      </DialogContent>

      {emailOpen && (
        <Dialog open onOpenChange={(v) => !v && setEmailOpen(false)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-[#234935]">Enviar relatório por e-mail</DialogTitle>
              <DialogDescription>
                O PDF é gerado agora e anexado ao e-mail. Separe múltiplos destinatários por vírgula, ponto-e-vírgula ou nova linha.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <div>
                <Label className="text-[11px] uppercase tracking-wider">Destinatários *</Label>
                <Textarea rows={3} value={destinatarios} onChange={(e) => setDestinatarios(e.target.value)}
                          placeholder="diretoria@empresa.com, cfo@empresa.com"
                          data-testid="email-destinatarios" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider">Mensagem (opcional)</Label>
                <Textarea rows={3} value={emailMessage} onChange={(e) => setEmailMessage(e.target.value)}
                          placeholder="Breve recado para a Diretoria"
                          data-testid="email-message" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setEmailOpen(false)} disabled={emailSending}>Cancelar</Button>
              <Button onClick={sendEmail} disabled={emailSending}
                      className="bg-[#234935] hover:bg-[#2c5c43] text-white" data-testid="btn-confirm-send-email">
                <Mail className="h-3.5 w-3.5 mr-1.5" />
                {emailSending ? "Enviando..." : "Enviar agora"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
}

// =============================================================================
// Model builder — fonte única de verdade (consome o result da simulação)
// =============================================================================
function buildReportModel(result, inputs, meta) {
  if (!result || !inputs) return null;
  const { clt, pj } = inputs;
  const r = result;
  const now = new Date();

  const fatorR = r.pj?.fator_r_aplicado ? "Folha ≥ 28%" : "Folha < 28% (Anexo V)";
  const composicao = r.pj?.tributos?.composicao || {};
  const tributosTotal = Number(r.pj?.total_tributos || 0);
  const custoContador = Number(r.pj?.custo_contador || 0);
  const totalDespesasPj = tributosTotal + custoContador;

  const proventos = r.clt?.proventos || {};
  const descontos = r.clt?.descontos || {};
  const encargos = r.clt?.encargos_empresa || {};

  const totalProventosClt = Number(proventos.total_proventos_mensal || 0);
  const totalDescontosClt = Number(descontos.total || 0);
  const totalEncargosClt = Number(encargos.total || 0);

  const cm = r.comparativo || {};
  const custoCe = cm.custo_empresa || {};
  const liquidoCp = cm.liquido_colaborador || {};

  // Síntese dinâmica
  const economiaPct = Number(custoCe.economia_pct || 0);
  const economiaMensal = Number(custoCe.economia_mensal || 0);
  const economiaAnual = Number(custoCe.economia_anual || 0);
  const diffLiquidoPct = Number(liquidoCp.diferenca_pct || 0);
  const diffLiquidoAnual = Number(liquidoCp.diferenca_anual || 0);
  const diffLiquidoMensal = (Number(liquidoCp.pj_mensal || 0) - Number(liquidoCp.clt_mensal || 0));

  const pjMaisEconomico = economiaAnual > 0;
  const pjLiquidoMaior = Number(liquidoCp.pj_mensal || 0) > Number(liquidoCp.clt_mensal || 0);

  let sintese;
  if (pjMaisEconomico) {
    sintese = `Considerando os parâmetros informados nesta simulação, a contratação PJ apresenta redução estimada de ${PCT(Math.abs(economiaPct))} no custo empresarial, correspondente a aproximadamente ${BRL(Math.abs(economiaAnual))} ao ano, enquanto o valor líquido econômico destinado ao profissional é ${PCT(Math.abs(diffLiquidoPct))} ${pjLiquidoMaior ? "superior" : "inferior"} ao cenário CLT analisado.`;
  } else {
    sintese = `Considerando os parâmetros informados nesta simulação, a contratação CLT apresenta custo empresarial ${PCT(Math.abs(economiaPct))} menor que a modalidade PJ, correspondente a aproximadamente ${BRL(Math.abs(economiaAnual))} ao ano. O líquido do profissional em CLT é ${PCT(Math.abs(diffLiquidoPct))} ${pjLiquidoMaior ? "inferior" : "superior"} ao PJ nesta simulação.`;
  }

  return {
    header: {
      titulo: "RELATÓRIO COMPARATIVO",
      subtitulo: "CONTRATAÇÃO PJ × CONTRATAÇÃO CLT",
      subheader: "ANÁLISE DE CUSTOS E IMPACTOS",
      vigencia: String(r.vigencia_tabelas || r.competencia_ano || new Date().getFullYear()),
      empresa: meta?.empresa || "",
      cargo: meta?.cargo || "",
      responsavel: meta?.responsavel || "",
      logoUrl: meta?.logoUrl || "",
      dataSimulacao: now.toLocaleDateString("pt-BR"),
      legalBasis: r.legal_basis || "",
    },
    pj: {
      valorMensalNf: Number(pj.valor_mensal || 0),
      regime: r.pj?.anexo || "—",
      issPct: Number(pj.iss_pct || 0) * (pj.iss_pct > 1 ? 1 : 100),
      rbt12: Number(pj.rbt12 || 0),
      custoContador: custoContador,
      fatorR,
      composicao,
      aliquotaEfetiva: Number(r.pj?.tributos?.aliquota_efetiva_pct || 0),
      totalTributos: tributosTotal,
      totalDespesas: totalDespesasPj,
      liquidoColaboradorMensal: Number(r.pj?.liquido_colaborador_mensal || 0),
      custoEmpresaMensal: Number(r.pj?.custo_empresa_mensal || 0),
      custoEmpresaAnual: Number(r.pj?.custo_empresa_anual || 0),
      observacoes: [
        "Sem férias",
        "Sem 13º salário",
        "Sem FGTS",
        r.pj?.sem_inss_irrf_pessoa_fisica ? "Sem INSS/IRRF de pessoa física" : "INSS/IRRF-PF considerado",
        custoContador > 0 ? `Custo do contador: ${BRL(custoContador)} / mês incluído no custo empresa` : "Sem custo contábil considerado",
      ],
    },
    clt: {
      salarioBase: Number(clt.salario_base || 0),
      beneficios: Number(clt.beneficios || 0),
      dependentes: Number(clt.dependentes || 0),
      inssPatronalPct: Number(encargos.inss_patronal_rate || 0.2) * 100,
      ratFapPct: Number(clt.rat_fap || 0) * (clt.rat_fap > 1 ? 1 : 100),
      terceirosPct: Number(clt.terceiros || 0) * (clt.terceiros > 1 ? 1 : 100),
      aposentadoriaEspecial: !!clt.aposentadoria_especial,
      proventos,
      descontos,
      encargos,
      totalProventos: totalProventosClt,
      totalDescontos: totalDescontosClt,
      totalEncargos: totalEncargosClt,
      liquidoFolha: Number(r.clt?.liquido_folha_mensal || 0),
      liquidoProvisionado: Number(r.clt?.liquido_provisionado_mensal || 0),
      custoEmpresaMensal: Number(r.clt?.custo_empresa_mensal || 0),
      custoEmpresaAnual: Number(r.clt?.custo_empresa_anual || 0),
    },
    comparativo: {
      liquidoClt: Number(liquidoCp.clt_mensal || 0),
      liquidoPj: Number(liquidoCp.pj_mensal || 0),
      custoEmpresaCltAnual: Number(custoCe.clt_anual || 0),
      custoEmpresaPjAnual: Number(custoCe.pj_anual || 0),
      economiaPct,
      economiaMensal,
      economiaAnual,
      diffLiquidoPct,
      diffLiquidoMensal,
      diffLiquidoAnual,
      pjMaisEconomico,
      pjLiquidoMaior,
    },
    sintese,
  };
}

function todayStamp() {
  const d = new Date();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}-${String(d.getHours()).padStart(2, "0")}${String(d.getMinutes()).padStart(2, "0")}`;
}

// =============================================================================
// Report document (HTML) — 1180px de largura (equivalente a A4 landscape)
// =============================================================================
function ReportDocument({ data }) {
  const { header, pj, clt, comparativo, sintese } = data;
  return (
    <div style={{
      width: 1180,
      fontFamily: '"Segoe UI", "Helvetica Neue", Arial, sans-serif',
      color: "#1a231f",
      background: "#ffffff",
      padding: 32,
      boxSizing: "border-box",
      fontSize: 11.5,
      lineHeight: 1.4,
    }}>
      {/* HEADER */}
      <div style={{ borderBottom: "3px solid #234935", paddingBottom: 14, marginBottom: 18, display: "flex", gap: 16, alignItems: "flex-start" }}>
        {header.logoUrl && (
          <img src={header.logoUrl} alt="Logo"
               crossOrigin="anonymous"
               style={{ height: 64, width: "auto", maxWidth: 150, objectFit: "contain",
                        border: "1px solid #e5e7eb", borderRadius: 6, padding: 4, background: "#fff" }}
               onError={(e) => { e.currentTarget.style.display = "none"; }} />
        )}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 10, letterSpacing: 2, color: "#8A6B2B", fontWeight: 700, marginBottom: 2 }}>
            THEOKRATOS RH · INTELIGÊNCIA FINANCEIRA
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, color: "#234935", letterSpacing: 0.6 }}>{header.titulo}</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#1a231f" }}>{header.subtitulo}</div>
          <div style={{ fontSize: 11.5, color: "#5a6b63", marginTop: 2 }}>{header.subheader}</div>
          <div style={{ display: "flex", gap: 18, flexWrap: "wrap", marginTop: 10, fontSize: 10.5 }}>
            <MetaItem label="VIGÊNCIA" value={header.vigencia} />
            {header.empresa && <MetaItem label="EMPRESA" value={header.empresa} />}
            {header.cargo && <MetaItem label="CARGO/FUNÇÃO" value={header.cargo} />}
            <MetaItem label="DATA SIMULAÇÃO" value={header.dataSimulacao} />
            {header.responsavel && <MetaItem label="RESPONSÁVEL" value={header.responsavel} />}
            {header.legalBasis && <MetaItem label="BASE LEGAL" value={header.legalBasis} />}
          </div>
        </div>
      </div>

      {/* DUAS COLUNAS */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        {/* ------- COLUNA PJ (azul) ------- */}
        <section style={cardStyle("#0F3B6B", "#E8F0FA")}>
          <SectionHeader color="#0F3B6B" title="CONTRATAÇÃO PJ" subtitle="PESSOA JURÍDICA" />
          <KV>
            <Row label="Valor mensal NF" value={BRL(pj.valorMensalNf)} />
            <Row label="Regime Tributário" value={pj.regime} />
            <Row label="ISS Municipal" value={PCT(pj.issPct)} />
            {pj.rbt12 > 0 && <Row label="RBT12 (12 meses)" value={BRL(pj.rbt12)} />}
            <Row label="Custo Contador" value={BRL(pj.custoContador)} />
            <Row label="Fator R Aplicado" value={pj.fatorR} />
          </KV>
          <SubTitle color="#0F3B6B">COMPOSIÇÃO DOS IMPOSTOS · {pj.regime.toUpperCase()}</SubTitle>
          <KV>
            <Row label="Valor mensal NF" value={BRL(pj.valorMensalNf)} />
            <Row label={`(-) Alíquota efetiva (${PCT(pj.aliquotaEfetiva)})`} value={BRL(-pj.totalTributos)} negative />
            {Object.entries(pj.composicao).map(([k, v]) => (
              <Row key={k} label={`     ${k}`} value={BRL(v)} dim />
            ))}
            {pj.custoContador > 0 && <Row label="(-) Custo contador" value={BRL(-pj.custoContador)} negative />}
          </KV>
          <TotalLine label="TOTAL DE TRIBUTOS" value={BRL(pj.totalTributos)} color="#0F3B6B" />
          <TotalLine label="TOTAL DE DESPESAS PJ" value={BRL(pj.totalDespesas)} color="#0F3B6B" strong />

          <SubTitle color="#0F3B6B">OBSERVAÇÕES</SubTitle>
          <ul style={{ margin: 0, paddingLeft: 16, fontSize: 10.5, color: "#2b3a33", lineHeight: 1.6 }}>
            {pj.observacoes.map((o, i) => <li key={i}>{o}</li>)}
          </ul>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 12 }}>
            <ResultCard color="#0F3B6B" bg="#E8F0FA" label="LÍQUIDO COLABORADOR PJ"
                        main={`${BRL(pj.liquidoColaboradorMensal)} / mês`} />
            <ResultCard color="#0F3B6B" bg="#E8F0FA" label="CUSTO EMPRESA PJ"
                        main={`${BRL(pj.custoEmpresaMensal)} / mês`}
                        secondary={`${BRL(pj.custoEmpresaAnual)} / ano`} />
          </div>
        </section>

        {/* ------- COLUNA CLT (verde) ------- */}
        <section style={cardStyle("#234935", "#E7F0EB")}>
          <SectionHeader color="#234935" title="CONTRATAÇÃO CLT" subtitle="CARTEIRA ASSINADA" />
          <KV>
            <Row label="Salário Base" value={BRL(clt.salarioBase)} />
            <Row label="Benefícios" value={BRL(clt.beneficios)} />
            <Row label="Dependentes IRRF" value={String(clt.dependentes)} />
            <Row label="INSS Patronal" value={PCT(clt.inssPatronalPct)} />
            <Row label="RAT/FAP" value={PCT(clt.ratFapPct)} />
            <Row label="Terceiros / Sistema S" value={PCT(clt.terceirosPct)} />
            <Row label="Aposentadoria Especial" value={clt.aposentadoriaEspecial ? "Sim" : "Não"} />
          </KV>

          <SubTitle color="#234935">PROVENTOS E PROVISÕES</SubTitle>
          <KV>
            <Row label="Salário mensal" value={BRL(clt.proventos.salario_base)} />
            <Row label="1/12 Férias" value={BRL(clt.proventos.ferias_1_12)} />
            <Row label="1/3 Constitucional de Férias" value={BRL(clt.proventos.terco_ferias)} />
            <Row label="1/12 13º Salário" value={BRL(clt.proventos.decimo_terceiro_1_12)} />
            <Row label="FGTS (8%)" value={BRL(clt.proventos.fgts)} />
            <Row label="Multa FGTS (40%) · provisão" value={BRL(clt.proventos.multa_fgts_provisao)} />
            {clt.beneficios > 0 && <Row label="Benefícios" value={BRL(clt.beneficios)} />}
          </KV>
          <TotalLine label="TOTAL DE PROVENTOS E PROVISÕES" value={BRL(clt.totalProventos)} color="#234935" strong />

          <SubTitle color="#234935">DESCONTOS DETALHADOS POR VERBA</SubTitle>
          <KV>
            <Row label={`INSS mensal (${PCT(clt.descontos.inss_mensal?.aliquota_efetiva)})`}
                 value={BRL(-clt.descontos.inss_mensal?.valor)} negative />
            <Row label={`IRRF mensal (alíq. ${PCT(clt.descontos.irrf_mensal?.aliquota)})`}
                 value={BRL(-clt.descontos.irrf_mensal?.valor)} negative />
            <Row label="INSS sobre 13º (rateado)" value={BRL(-clt.descontos.inss_13o?.valor_rateado)} negative dim />
            <Row label="INSS sobre Férias (rateado)" value={BRL(-clt.descontos.inss_ferias?.valor_rateado)} negative dim />
            <Row label="IRRF sobre 13º (rateado)" value={BRL(-clt.descontos.irrf_13o?.valor_rateado)} negative dim />
            <Row label="IRRF sobre Férias (rateado)" value={BRL(-clt.descontos.irrf_ferias?.valor_rateado)} negative dim />
          </KV>
          <TotalLine label="TOTAL DE DESCONTOS" value={BRL(-clt.totalDescontos)} color="#B91C1C" strong />

          <SubTitle color="#234935">ENCARGOS EMPRESA</SubTitle>
          <KV>
            <Row label={`INSS Patronal (${PCT(clt.inssPatronalPct)})`} value={BRL(clt.encargos.inss_patronal)} />
            <Row label={`RAT/FAP (${PCT(clt.ratFapPct)})`} value={BRL(clt.encargos.rat_fap)} />
            <Row label={`Sistema S / Terceiros (${PCT(clt.terceirosPct)})`} value={BRL(clt.encargos.terceiros)} />
            <Row label="FGTS (8%)" value={BRL(clt.encargos.fgts)} />
            <Row label="Multa FGTS (40%) · provisão" value={BRL(clt.encargos.multa_fgts_provisao)} />
          </KV>
          <TotalLine label="TOTAL DE ENCARGOS EMPRESA" value={BRL(clt.totalEncargos)} color="#234935" strong />

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginTop: 12 }}>
            <ResultCard color="#234935" bg="#E7F0EB" label="LÍQUIDO EM FOLHA"
                        main={`${BRL(clt.liquidoFolha)} / mês`}
                        hint="Recebimento mensal direto" />
            <ResultCard color="#234935" bg="#E7F0EB" label="PROVISIONADO / LÍQUIDO ECONÔMICO"
                        main={`${BRL(clt.liquidoProvisionado)}`}
                        hint="Base da comparação com PJ" />
            <ResultCard color="#234935" bg="#E7F0EB" label="CUSTO EMPRESA CLT"
                        main={`${BRL(clt.custoEmpresaMensal)} / mês`}
                        secondary={`${BRL(clt.custoEmpresaAnual)} / ano`} />
          </div>
        </section>
      </div>

      {/* RESULTADO COMPARATIVO FINAL */}
      <div style={{ marginTop: 18, border: "2px solid #234935", borderRadius: 10, padding: 14, background: "#F8F6ED" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#234935", letterSpacing: 1, marginBottom: 10 }}>
          RESULTADO COMPARATIVO FINAL
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10 }}>
          <ResultCard color="#234935" bg="#ffffff" label="LÍQUIDO CLT" main={`${BRL(comparativo.liquidoClt)}`} secondary="/ mês" />
          <ResultCard color="#0F3B6B" bg="#ffffff" label="LÍQUIDO PJ"  main={`${BRL(comparativo.liquidoPj)}`}  secondary="/ mês" />
          <ResultCard color="#234935" bg="#ffffff" label="CUSTO EMPRESA CLT" main={`${BRL(comparativo.custoEmpresaCltAnual)}`} secondary="/ ano" />
          <ResultCard color="#0F3B6B" bg="#ffffff" label="CUSTO EMPRESA PJ"  main={`${BRL(comparativo.custoEmpresaPjAnual)}`}  secondary="/ ano" />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
          <IndicatorCard
            title="ECONOMIA DA EMPRESA"
            pct={comparativo.economiaPct}
            mensal={comparativo.economiaMensal}
            anual={comparativo.economiaAnual}
            tone={comparativo.pjMaisEconomico ? "ok" : "warn"}
            label={comparativo.pjMaisEconomico ? "PJ reduz custo empresarial" : "CLT reduz custo empresarial"}
          />
          <IndicatorCard
            title="DIFERENÇA PARA O COLABORADOR"
            pct={comparativo.diffLiquidoPct}
            mensal={comparativo.diffLiquidoMensal}
            anual={comparativo.diffLiquidoAnual}
            tone={comparativo.pjLiquidoMaior ? "pj" : "clt"}
            label={comparativo.pjLiquidoMaior ? "PJ · líquido superior" : "CLT · líquido superior"}
          />
        </div>
      </div>

      {/* SÍNTESE EXECUTIVA */}
      <div style={{ marginTop: 14, borderLeft: "4px solid #C5A059", background: "#FBF7EC", padding: 12, borderRadius: 6 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#8A6B2B", letterSpacing: 1 }}>SÍNTESE EXECUTIVA</div>
        <div style={{ fontSize: 12, color: "#1a231f", marginTop: 4 }}>{sintese}</div>
      </div>

      {/* MEMÓRIA DE CÁLCULO */}
      <div style={{ marginTop: 14, border: "1px solid #d7d9d4", borderRadius: 8, padding: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#234935", letterSpacing: 1, marginBottom: 6 }}>MEMÓRIA DE CÁLCULO</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, fontSize: 10.5, color: "#2b3a33" }}>
          <MemoBlock title="PJ">
            <li>Líquido PJ = Valor NF − DAS Simples − Custo Contador</li>
            <li>DAS = Valor NF × alíq. efetiva ({PCT(pj.aliquotaEfetiva)}) → {BRL(pj.totalTributos)}</li>
            <li>Composição Simples: {Object.entries(pj.composicao).map(([k, v]) => `${k} ${BRL(v)}`).join(" · ")}</li>
            <li>Custo Empresa PJ = Valor NF ({BRL(pj.valorMensalNf)}) × 12 = {BRL(pj.custoEmpresaAnual)}</li>
          </MemoBlock>
          <MemoBlock title="CLT">
            <li>Proventos Totais = Salário + 1/12 Férias + 1/3 Férias + 1/12 13º + FGTS 8% + Multa FGTS 40% + Benefícios</li>
            <li>Descontos = INSS mensal + IRRF mensal + rateios INSS/IRRF 13º/Férias</li>
            <li>Encargos Patronais = INSS Patronal {PCT(clt.inssPatronalPct)} + RAT/FAP {PCT(clt.ratFapPct)} + Terceiros {PCT(clt.terceirosPct)} + FGTS 8% + Multa FGTS 40%</li>
            <li>Custo Mensal CLT = Proventos + Encargos Patronais = {BRL(clt.custoEmpresaMensal)}</li>
            <li>Custo Anual CLT = Custo Mensal × 12 = {BRL(clt.custoEmpresaAnual)}</li>
          </MemoBlock>
          <MemoBlock title="COMPARATIVO">
            <li>Economia Mensal Empresa = Custo CLT − Custo PJ = {BRL(comparativo.economiaMensal)}</li>
            <li>Economia Anual Empresa = Economia Mensal × 12 = {BRL(comparativo.economiaAnual)}</li>
            <li>Economia % = (CLT − PJ) ÷ CLT × 100 = {PCT(comparativo.economiaPct)}</li>
          </MemoBlock>
          <MemoBlock title="LÍQUIDO COLABORADOR">
            <li>Líquido CLT (econômico) = {BRL(comparativo.liquidoClt)} / mês</li>
            <li>Líquido PJ = Valor NF − DAS − Contador = {BRL(comparativo.liquidoPj)} / mês</li>
            <li>Diferença Mensal = {BRL(Math.abs(comparativo.diffLiquidoMensal))} ({comparativo.pjLiquidoMaior ? "PJ > CLT" : "CLT > PJ"})</li>
            <li>Diferença % = {PCT(comparativo.diffLiquidoPct)}</li>
          </MemoBlock>
        </div>
      </div>

      {/* Rodapé */}
      <div style={{ marginTop: 14, display: "flex", justifyContent: "space-between", borderTop: "1px solid #d7d9d4", paddingTop: 8, fontSize: 9.5, color: "#8A6B2B" }}>
        <span>THEOKRATOS RH · Relatório gerado automaticamente a partir da simulação ativa.</span>
        <span>{header.dataSimulacao} · {header.vigencia}</span>
      </div>
    </div>
  );
}

// ----- helpers visuais -----
const cardStyle = (color, bgSoft) => ({
  border: `2px solid ${color}`,
  borderRadius: 10,
  padding: 12,
  background: "#ffffff",
  position: "relative",
});
const SectionHeader = ({ color, title, subtitle }) => (
  <div style={{ background: color, color: "#fff", padding: "8px 12px", borderRadius: 6, marginBottom: 10 }}>
    <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 1 }}>{title}</div>
    <div style={{ fontSize: 10, letterSpacing: 1, opacity: 0.85 }}>{subtitle}</div>
  </div>
);
const SubTitle = ({ color, children }) => (
  <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 1, color, borderBottom: `1px solid ${color}33`,
                paddingBottom: 3, marginTop: 10, marginBottom: 6, textTransform: "uppercase" }}>{children}</div>
);
const KV = ({ children }) => <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>{children}</div>;
const Row = ({ label, value, negative, dim, bold }) => (
  <div style={{ display: "flex", justifyContent: "space-between", gap: 10,
                padding: "2px 0", fontSize: dim ? 10 : 11,
                color: dim ? "#5a6b63" : "#1a231f",
                fontWeight: bold ? 700 : 400 }}>
    <span>{label}</span>
    <span style={{
      fontVariantNumeric: "tabular-nums",
      color: negative ? "#B91C1C" : (bold ? "#234935" : (dim ? "#5a6b63" : "#1a231f")),
    }}>{value}</span>
  </div>
);
const TotalLine = ({ label, value, color, strong }) => (
  <div style={{
    display: "flex", justifyContent: "space-between",
    padding: "6px 8px",
    marginTop: 6,
    background: strong ? color : "#f0f3ec",
    color: strong ? "#fff" : color,
    borderRadius: 4,
    fontWeight: 700,
    fontSize: 11.5,
    letterSpacing: 0.3,
  }}>
    <span>{label}</span>
    <span style={{ fontVariantNumeric: "tabular-nums" }}>{value}</span>
  </div>
);
const ResultCard = ({ color, bg, label, main, secondary, hint }) => (
  <div style={{ border: `1px solid ${color}66`, background: bg, borderRadius: 8, padding: 10 }}>
    <div style={{ fontSize: 9.5, letterSpacing: 1, color, fontWeight: 700, textTransform: "uppercase" }}>{label}</div>
    <div style={{ fontSize: 16, fontWeight: 700, color: "#1a231f", marginTop: 3, fontVariantNumeric: "tabular-nums" }}>{main}</div>
    {secondary && <div style={{ fontSize: 11, color: "#5a6b63", fontVariantNumeric: "tabular-nums" }}>{secondary}</div>}
    {hint && <div style={{ fontSize: 9.5, color: "#8A6B2B", marginTop: 4, fontStyle: "italic" }}>{hint}</div>}
  </div>
);
const IndicatorCard = ({ title, pct, mensal, anual, label, tone }) => {
  const colors = {
    ok:   { bg: "#E7F0EB", bd: "#234935", fg: "#234935" },
    warn: { bg: "#F8F6ED", bd: "#8A6B2B", fg: "#8A6B2B" },
    pj:   { bg: "#E8F0FA", bd: "#0F3B6B", fg: "#0F3B6B" },
    clt:  { bg: "#E7F0EB", bd: "#234935", fg: "#234935" },
  }[tone] || { bg: "#f0f3ec", bd: "#234935", fg: "#234935" };
  return (
    <div style={{ background: colors.bg, border: `1px solid ${colors.bd}`, borderRadius: 8, padding: 12 }}>
      <div style={{ fontSize: 10, letterSpacing: 1, fontWeight: 700, color: colors.fg }}>{title}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color: colors.fg, marginTop: 2, fontVariantNumeric: "tabular-nums" }}>
        {PCT(Math.abs(pct))}
      </div>
      <div style={{ fontSize: 11, color: "#2b3a33", fontVariantNumeric: "tabular-nums" }}>
        {BRL(Math.abs(mensal))} / mês · {BRL(Math.abs(anual))} / ano
      </div>
      <div style={{ fontSize: 10, color: colors.fg, marginTop: 3, fontWeight: 600 }}>{label}</div>
    </div>
  );
};
const MetaItem = ({ label, value }) => (
  <div>
    <div style={{ fontSize: 8.5, letterSpacing: 1.4, color: "#8A6B2B", fontWeight: 700 }}>{label}</div>
    <div style={{ fontSize: 11, color: "#1a231f", fontWeight: 600 }}>{value}</div>
  </div>
);
const MemoBlock = ({ title, children }) => (
  <div>
    <div style={{ fontSize: 10, fontWeight: 700, color: "#234935", letterSpacing: 1, marginBottom: 2 }}>{title}</div>
    <ul style={{ margin: 0, paddingLeft: 16, lineHeight: 1.5 }}>{children}</ul>
  </div>
);

// =============================================================================
// DOCX export — editável, com tabelas e identidade visual
// =============================================================================
async function buildDocx(data) {
  const {
    Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell, WidthType,
    AlignmentType, HeadingLevel, BorderStyle, PageOrientation,
  } = await import("docx");

  const C_GREEN = "234935";
  const C_BLUE = "0F3B6B";
  const C_GOLD = "8A6B2B";

  const txt = (text, opts = {}) => new TextRun({ text, ...opts });
  const P = (children, opts = {}) => new Paragraph({ children, ...opts });

  const cell = (text, { color, bg, bold, align, border = true } = {}) =>
    new TableCell({
      shading: bg ? { fill: bg } : undefined,
      children: [new Paragraph({
        alignment: align || AlignmentType.LEFT,
        children: [new TextRun({ text, color, bold })],
      })],
      borders: border ? undefined : {
        top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
        left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
      },
    });

  const kvTable = (rows, headerColor) =>
    new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: rows.map(([k, v]) => new TableRow({
        children: [cell(k, { color: "1a231f" }), cell(v, { color: headerColor, bold: true, align: AlignmentType.RIGHT })],
      })),
    });

  const sectionHeader = (text, color) =>
    new Paragraph({
      spacing: { before: 240, after: 120 },
      shading: { fill: color },
      children: [new TextRun({ text, bold: true, color: "FFFFFF", size: 22 })],
    });

  const subTitle = (text, color) =>
    new Paragraph({
      spacing: { before: 180, after: 80 },
      children: [new TextRun({ text, bold: true, color, size: 20 })],
    });

  const h = data.header, pj = data.pj, clt = data.clt, c = data.comparativo;

  const metaRows = [
    ["Vigência", h.vigencia],
    ["Data Simulação", h.dataSimulacao],
  ];
  if (h.empresa) metaRows.push(["Empresa", h.empresa]);
  if (h.cargo) metaRows.push(["Cargo/Função", h.cargo]);
  if (h.responsavel) metaRows.push(["Responsável", h.responsavel]);
  if (h.legalBasis) metaRows.push(["Base Legal", h.legalBasis]);

  const pjParamsRows = [
    ["Valor mensal NF", BRL(pj.valorMensalNf)],
    ["Regime Tributário", pj.regime],
    ["ISS Municipal", PCT(pj.issPct)],
    ...(pj.rbt12 > 0 ? [["RBT12", BRL(pj.rbt12)]] : []),
    ["Custo Contador", BRL(pj.custoContador)],
    ["Fator R Aplicado", pj.fatorR],
  ];

  const pjImpostosRows = [
    ["Valor mensal NF", BRL(pj.valorMensalNf)],
    [`(-) Alíquota efetiva (${PCT(pj.aliquotaEfetiva)})`, BRL(-pj.totalTributos)],
    ...Object.entries(pj.composicao).map(([k, v]) => [`     ${k}`, BRL(v)]),
    ...(pj.custoContador > 0 ? [["(-) Custo contador", BRL(-pj.custoContador)]] : []),
    ["TOTAL DE TRIBUTOS", BRL(pj.totalTributos)],
    ["TOTAL DE DESPESAS PJ", BRL(pj.totalDespesas)],
  ];

  const cltParamsRows = [
    ["Salário Base", BRL(clt.salarioBase)],
    ["Benefícios", BRL(clt.beneficios)],
    ["Dependentes IRRF", String(clt.dependentes)],
    ["INSS Patronal", PCT(clt.inssPatronalPct)],
    ["RAT/FAP", PCT(clt.ratFapPct)],
    ["Terceiros / Sistema S", PCT(clt.terceirosPct)],
    ["Aposentadoria Especial", clt.aposentadoriaEspecial ? "Sim" : "Não"],
  ];
  const proventosRows = [
    ["Salário mensal", BRL(clt.proventos.salario_base)],
    ["1/12 Férias", BRL(clt.proventos.ferias_1_12)],
    ["1/3 Constitucional de Férias", BRL(clt.proventos.terco_ferias)],
    ["1/12 13º Salário", BRL(clt.proventos.decimo_terceiro_1_12)],
    ["FGTS (8%)", BRL(clt.proventos.fgts)],
    ["Multa FGTS (40%) · provisão", BRL(clt.proventos.multa_fgts_provisao)],
    ...(clt.beneficios > 0 ? [["Benefícios", BRL(clt.beneficios)]] : []),
    ["TOTAL DE PROVENTOS E PROVISÕES", BRL(clt.totalProventos)],
  ];
  const descontosRows = [
    [`INSS mensal (${PCT(clt.descontos.inss_mensal?.aliquota_efetiva)})`, BRL(-clt.descontos.inss_mensal?.valor)],
    [`IRRF mensal (${PCT(clt.descontos.irrf_mensal?.aliquota)})`, BRL(-clt.descontos.irrf_mensal?.valor)],
    ["INSS 13º (rateado)", BRL(-clt.descontos.inss_13o?.valor_rateado)],
    ["INSS Férias (rateado)", BRL(-clt.descontos.inss_ferias?.valor_rateado)],
    ["IRRF 13º (rateado)", BRL(-clt.descontos.irrf_13o?.valor_rateado)],
    ["IRRF Férias (rateado)", BRL(-clt.descontos.irrf_ferias?.valor_rateado)],
    ["TOTAL DE DESCONTOS", BRL(-clt.totalDescontos)],
  ];
  const encargosRows = [
    [`INSS Patronal (${PCT(clt.inssPatronalPct)})`, BRL(clt.encargos.inss_patronal)],
    [`RAT/FAP (${PCT(clt.ratFapPct)})`, BRL(clt.encargos.rat_fap)],
    [`Sistema S / Terceiros (${PCT(clt.terceirosPct)})`, BRL(clt.encargos.terceiros)],
    ["FGTS (8%)", BRL(clt.encargos.fgts)],
    ["Multa FGTS (40%) · provisão", BRL(clt.encargos.multa_fgts_provisao)],
    ["TOTAL DE ENCARGOS EMPRESA", BRL(clt.totalEncargos)],
  ];

  const finalRows = [
    ["LÍQUIDO CLT (mensal)", BRL(c.liquidoClt)],
    ["LÍQUIDO PJ (mensal)", BRL(c.liquidoPj)],
    ["CUSTO EMPRESA CLT (anual)", BRL(c.custoEmpresaCltAnual)],
    ["CUSTO EMPRESA PJ (anual)", BRL(c.custoEmpresaPjAnual)],
    ["ECONOMIA EMPRESA %", PCT(Math.abs(c.economiaPct))],
    ["ECONOMIA EMPRESA (mês)", BRL(Math.abs(c.economiaMensal))],
    ["ECONOMIA EMPRESA (ano)", BRL(Math.abs(c.economiaAnual))],
    ["DIFERENÇA LÍQUIDO COLABORADOR %", PCT(Math.abs(c.diffLiquidoPct))],
    ["DIFERENÇA LÍQUIDO COLABORADOR (ano)", BRL(Math.abs(c.diffLiquidoAnual))],
    ["MODALIDADE LÍQUIDO SUPERIOR", c.pjLiquidoMaior ? "PJ" : "CLT"],
    ["MODALIDADE CUSTO EMPRESARIAL MENOR", c.pjMaisEconomico ? "PJ" : "CLT"],
  ];

  const children = [
    P([txt("THEOKRATOS RH · INTELIGÊNCIA FINANCEIRA", { color: C_GOLD, bold: true, size: 18 })]),
    new Paragraph({
      heading: HeadingLevel.HEADING_1,
      children: [new TextRun({ text: h.titulo, bold: true, color: C_GREEN, size: 36 })],
    }),
    new Paragraph({
      children: [new TextRun({ text: h.subtitulo, bold: true, color: "1a231f", size: 28 })],
    }),
    P([txt(h.subheader, { color: "5a6b63", size: 22 })]),
    P([txt(" ")]),
    kvTable(metaRows, C_GOLD),

    sectionHeader("CONTRATAÇÃO PJ · PESSOA JURÍDICA", C_BLUE),
    kvTable(pjParamsRows, C_BLUE),
    subTitle("COMPOSIÇÃO DOS IMPOSTOS", C_BLUE),
    kvTable(pjImpostosRows, C_BLUE),
    subTitle("OBSERVAÇÕES", C_BLUE),
    ...pj.observacoes.map((o) => P([txt("• " + o, { size: 20 })])),
    subTitle("RESULTADO PJ", C_BLUE),
    kvTable([
      ["Líquido Colaborador PJ (mês)", BRL(pj.liquidoColaboradorMensal)],
      ["Custo Empresa PJ (mês)", BRL(pj.custoEmpresaMensal)],
      ["Custo Empresa PJ (ano)", BRL(pj.custoEmpresaAnual)],
    ], C_BLUE),

    sectionHeader("CONTRATAÇÃO CLT · CARTEIRA ASSINADA", C_GREEN),
    kvTable(cltParamsRows, C_GREEN),
    subTitle("PROVENTOS E PROVISÕES", C_GREEN),
    kvTable(proventosRows, C_GREEN),
    subTitle("DESCONTOS DETALHADOS POR VERBA", C_GREEN),
    kvTable(descontosRows, "B91C1C"),
    subTitle("ENCARGOS EMPRESA", C_GREEN),
    kvTable(encargosRows, C_GREEN),
    subTitle("RESULTADO CLT", C_GREEN),
    kvTable([
      ["Líquido em Folha (mês)", BRL(clt.liquidoFolha)],
      ["Provisionado / Líquido Econômico", BRL(clt.liquidoProvisionado)],
      ["Custo Empresa CLT (mês)", BRL(clt.custoEmpresaMensal)],
      ["Custo Empresa CLT (ano)", BRL(clt.custoEmpresaAnual)],
    ], C_GREEN),

    sectionHeader("RESULTADO COMPARATIVO FINAL", C_GREEN),
    kvTable(finalRows, C_GREEN),

    subTitle("SÍNTESE EXECUTIVA", C_GOLD),
    P([txt(data.sintese, { size: 22 })]),

    subTitle("MEMÓRIA DE CÁLCULO", C_GREEN),
    P([txt("PJ", { bold: true, color: C_BLUE, size: 22 })]),
    P([txt("• Líquido PJ = Valor NF − DAS Simples − Custo Contador", { size: 20 })]),
    P([txt(`• DAS = Valor NF × ${PCT(pj.aliquotaEfetiva)} = ${BRL(pj.totalTributos)}`, { size: 20 })]),
    P([txt(`• Composição Simples: ${Object.entries(pj.composicao).map(([k, v]) => `${k} ${BRL(v)}`).join(" · ")}`, { size: 20 })]),
    P([txt(`• Custo Empresa PJ = Valor NF × 12 = ${BRL(pj.custoEmpresaAnual)}`, { size: 20 })]),
    P([txt("CLT", { bold: true, color: C_GREEN, size: 22 })]),
    P([txt("• Proventos = Salário + 1/12 Férias + 1/3 Férias + 1/12 13º + FGTS 8% + Multa FGTS 40% + Benefícios", { size: 20 })]),
    P([txt(`• Encargos Patronais = INSS ${PCT(clt.inssPatronalPct)} + RAT/FAP ${PCT(clt.ratFapPct)} + Terceiros ${PCT(clt.terceirosPct)} + FGTS 8% + Multa FGTS 40%`, { size: 20 })]),
    P([txt(`• Custo Mensal CLT = Proventos + Encargos = ${BRL(clt.custoEmpresaMensal)}`, { size: 20 })]),
    P([txt(`• Custo Anual CLT = Custo Mensal × 12 = ${BRL(clt.custoEmpresaAnual)}`, { size: 20 })]),
    P([txt("COMPARATIVO", { bold: true, color: C_GOLD, size: 22 })]),
    P([txt(`• Economia Mensal Empresa = ${BRL(Math.abs(c.economiaMensal))}`, { size: 20 })]),
    P([txt(`• Economia Anual Empresa = ${BRL(Math.abs(c.economiaAnual))}`, { size: 20 })]),
    P([txt(`• Economia % = (CLT − PJ) ÷ CLT × 100 = ${PCT(Math.abs(c.economiaPct))}`, { size: 20 })]),

    P([txt("")]),
    P([txt("THEOKRATOS RH · Relatório gerado automaticamente a partir da simulação ativa.", { color: C_GOLD, italics: true, size: 18 })]),
  ];

  const doc = new Document({
    sections: [{
      properties: {
        page: { size: { orientation: PageOrientation.LANDSCAPE } },
      },
      children,
    }],
  });
  return await Packer.toBlob(doc);
}
